import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import * as DataAccessFairData from '@planone/bau365/data-access-fair-data';
import {
  DataAccessEventsService,
  DataAccessExhibitorService,
  DataAccessExhibitorsFacade,
  DataAccessFairsFacade,
  DataAccessPostsService,
  DataAccessProductsFacade,
  DataAccessProductsService,
} from '@planone/bau365/data-access-fair-data';
import { EntityType } from '@planone/bau365/util-fair-data';
import { NotificationService } from '@planone/bau365/util-notification';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { SnackBarType } from '@planone/shared/util-interfaces';
import { BehaviorSubject, combineLatestWith, of, withLatestFrom } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import * as WatchlistActions from './watchlist.actions';
import { WatchlistActionTrigger } from './watchlist.models';

import { USER_SESSION_STATUS } from '../../config/user-service.config.interface';
import { WatchlistService } from '../../service/watchlist/watchlist.service';
import * as UserActions from '../user/data-access-user.actions';

@Injectable()
export class WatchlistEffects {
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userIsLoggedIn),
      withLatestFrom(this.features.flag('kill-watchlist')),
      filter(([_, watchlistDisabled]) => !watchlistDisabled),
      switchMap(() =>
        this.watchlistService.getWatchlistItems().pipe(
          map((items) => {
            return {
              ...items,
              items: items.items.map((item) => {
                return {
                  ...item,
                  entity_id: item.entity_id.includes('-slash-')
                    ? item.entity_id.replace(/-slash-/g, '/')
                    : item.entity_id,
                };
              }),
            };
          }),
          map(({ items }) => WatchlistActions.loadWatchlistSuccess({ items })),
          catchError((error) => of(WatchlistActions.loadWatchlistFailure(error)))
        )
      )
    )
  );

  addWatchlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.addWatchlistItemRequested),
      withLatestFrom(this.userSessionStatus),
      filter(([_, activeSession]) => activeSession),
      mergeMap(([watchlistItemInfo]) =>
        this.watchlistService.addWatchlistItem(watchlistItemInfo.watchlistItem).pipe(
          map((item) => {
            return {
              ...item,
              entity_id: item.entity_id.includes('-slash-') ? item.entity_id.replace(/-slash-/g, '/') : item.entity_id,
            };
          }),
          map((item) =>
            WatchlistActions.addWatchlistItemSuccess({
              watchlistItem: item,
              pageContext: watchlistItemInfo.pageContext,
              trigger: WatchlistActionTrigger.ADD,
            })
          ),
          catchError((error) => of(WatchlistActions.addWatchlistItemFailure(error)))
        )
      )
    )
  );

  addWatchlistItemSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.addWatchlistItemSuccess),
      map((action) => {
        this.notificationService.projectContent(
          $localize`:@@watchlistEffect-addItemSuccess:Zur Merkliste hinzugefügt`,
          SnackBarType.success,
          undefined,
          true
        );

        return WatchlistActions.loadWatchlistEntity(action);
      })
    )
  );

  addWatchlistItemFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WatchlistActions.addWatchlistItemFailure),
        tap(() => {
          this.notificationService.projectContent(
            $localize`:@@watchlistEffect-addItemFailure:Zur Merkliste hinzufügen fehlgeschlagen`,
            SnackBarType.failure,
            undefined,
            true
          );
        })
      ),
    { dispatch: false }
  );

  removeWatchlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.removeWatchlistItemRequested, WatchlistActions.deleteWatchlistItemFromList),
      mergeMap((watchlistItemInfo) =>
        this.watchlistService.removeWatchlistItem(watchlistItemInfo.watchlistItem).pipe(
          map(() =>
            WatchlistActions.removeWatchlistItemSuccess({
              ...watchlistItemInfo,
              trigger: WatchlistActionTrigger.REMOVE,
            })
          ),
          catchError((error) => of(WatchlistActions.removeWatchlistItemFailure(error)))
        )
      )
    )
  );

  removeWatchlistItemSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.removeWatchlistItemSuccess),
      map((action) => {
        this.notificationService.projectContent(
          $localize`:@@watchlistEffect-removeItemSuccess:Von Merkliste entfernt`,
          SnackBarType.neutral,
          undefined,
          true
        );

        return WatchlistActions.loadWatchlistEntity(action);
      })
    )
  );

  shareWatchlistItemSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.shareWatchlistItemFromList),
      map((action) => {
        return WatchlistActions.loadWatchlistEntity({ ...action, trigger: WatchlistActionTrigger.SHARE });
      })
    )
  );

  shareActionWatchlistItemSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.shareActionWatchlistItemFromList),
      map((action) => {
        return WatchlistActions.loadWatchlistEntity({
          ...action,
          trigger: WatchlistActionTrigger.SHARE_ACTION,
          network: action.network,
        });
      })
    )
  );

  removeWatchlistItemFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WatchlistActions.removeWatchlistItemFailure),
        tap(() => {
          this.notificationService.projectContent(
            $localize`:@@watchlistEffect-removeItemFailure:Von Merkliste entfernen fehlgeschlagen`,
            SnackBarType.failure,
            undefined,
            true
          );
        })
      ),
    { dispatch: false }
  );

  loadEventWatchlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.loadWatchlistEntity),
      filter((action) => action.watchlistItem.entity_type === 'EVENT'),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(DataAccessFairData.getEventById, { eventId: action.watchlistItem.entity_id }))
          )
        )
      ),
      switchMap(([action, event]) =>
        (event
          ? of(event)
          : this.eventsService.getEvent(action.watchlistItem.fair_id, action.watchlistItem.entity_id)
        ).pipe(
          switchMap((event) =>
            of(event).pipe(
              withLatestFrom(
                this.store.pipe(select(DataAccessFairData.getExhibitorById, { exhibitorId: event.exhibitor_id }))
              )
            )
          ),
          switchMap(([event, exhibitor]) =>
            (exhibitor
              ? of(exhibitor)
              : this.exhibitorService.getExhibitor(action.watchlistItem.fair_id, event.exhibitor_id)
            ).pipe(
              map((exhibitor) =>
                WatchlistActions.loadWatchlistEntitySuccess({
                  ...action,
                  exhibitor: exhibitor,
                  event: event,
                })
              ),
              catchError(() => of(WatchlistActions.loadWatchlistEntityFailure(action)))
            )
          ),
          catchError(() => of(WatchlistActions.loadWatchlistEntityFailure(action)))
        )
      )
    )
  );

  addWatchlistQRCodeEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessFairData.productsPageView),
      withLatestFrom(this.activatedRoute.queryParams, this.fairsFacade.fairId$),
      combineLatestWith(this.productFacade.product$),
      filter(([[_, queryParams], product]) => !!product && queryParams['utm_medium'] === 'qr-code'),
      switchMap(([[_, __, fairId], product]) => {
        return of(
          WatchlistActions.addWatchlistItemRequested({
            watchlistItem: {
              entity_id: product?.id ?? '',
              entity_type: EntityType.product,
              fair_id: fairId,
            },
            pageContext: 'product-details',
          })
        );
      })
    )
  );

  addWatchlistEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessFairData.appOpened),
      withLatestFrom(this.activatedRoute.queryParams),
      filter(([_action, queryParams]) => queryParams['watchlist'] === '1'),
      switchMap(([_action, queryParams]) => {
        return of(
          WatchlistActions.addWatchlistItemRequested({
            watchlistItem: {
              entity_id: queryParams['entityId'],
              entity_type: queryParams['entityType'],
              fair_id: queryParams['fairId'],
            },
            pageContext: queryParams['pageContext'],
          })
        );
      })
    )
  );

  loadExhibitorWatchlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.loadWatchlistEntity),
      filter((action) => action.watchlistItem.entity_type === 'EXHIBITOR'),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(
              select(DataAccessFairData.getExhibitorById, { exhibitorId: action.watchlistItem.entity_id })
            )
          )
        )
      ),
      switchMap(([action, exhibitor]) =>
        (exhibitor
          ? of(exhibitor)
          : this.exhibitorService.getExhibitor(action.watchlistItem.fair_id, action.watchlistItem.entity_id)
        ).pipe(
          map((exhibitor) =>
            WatchlistActions.loadWatchlistEntitySuccess({
              ...action,
              exhibitor: exhibitor,
            })
          ),
          catchError(() => of(WatchlistActions.loadWatchlistEntityFailure(action)))
        )
      )
    )
  );

  loadBrandWatchlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.loadWatchlistEntity),
      filter((action) => action.watchlistItem.entity_type === 'BRAND'),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(
              select(DataAccessFairData.getExhibitorById, { exhibitorId: action.watchlistItem.entity_id.slice(0, 36) })
            )
          )
        )
      ),
      switchMap(([action, exhibitor]) =>
        (exhibitor
          ? of(exhibitor)
          : this.exhibitorService.getExhibitor(
              action.watchlistItem.fair_id,
              action.watchlistItem.entity_id.slice(0, 36)
            )
        ).pipe(
          map((exhibitor) =>
            WatchlistActions.loadWatchlistEntitySuccess({
              ...action,
              brand: {
                ...exhibitor,
                name: action.watchlistItem.entity_id.slice(37),
              },
            })
          ),
          catchError(() => of(WatchlistActions.loadWatchlistEntityFailure(action)))
        )
      )
    )
  );

  loadPostWatchlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.loadWatchlistEntity),
      filter((action) => action.watchlistItem.entity_type === 'POST'),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(DataAccessFairData.getPostById, { postId: action.watchlistItem.entity_id }))
          )
        )
      ),
      switchMap(([action, post]) =>
        (post
          ? of(post)
          : this.postsService.getPost(action.watchlistItem.fair_id, action.watchlistItem.entity_id)
        ).pipe(
          switchMap((post) =>
            of(post).pipe(
              withLatestFrom(
                this.store.pipe(select(DataAccessFairData.getExhibitorById, { exhibitorId: post.exhibitor_id })),
                of(action.watchlistItem.fair_id)
              )
            )
          ),
          switchMap(([post, exhibitor, fairId]) =>
            (exhibitor ? of(exhibitor) : this.exhibitorService.getExhibitor(fairId, post.exhibitor_id)).pipe(
              map((exhibitor) =>
                WatchlistActions.loadWatchlistEntitySuccess({
                  ...action,
                  exhibitor: exhibitor,
                  post: post,
                })
              ),
              catchError(() => of(WatchlistActions.loadWatchlistEntityFailure(action)))
            )
          ),
          catchError(() => of(WatchlistActions.loadWatchlistEntityFailure(action)))
        )
      )
    )
  );

  addWatchlistExhibitorEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessFairData.exhibitorsPageView),
      withLatestFrom(this.activatedRoute.queryParams, this.fairsFacade.fairId$),
      combineLatestWith(this.exhibitorFacade.exhibitor$),
      filter(([[_, queryParams], exhibitor]) => !!exhibitor && queryParams['utm_medium'] === 'qr-code'),
      switchMap(([[_, __, fairId], exhibitor]) => {
        return of(
          WatchlistActions.addWatchlistItemRequested({
            watchlistItem: {
              entity_id: exhibitor?.id ?? '',
              entity_type: EntityType.exhibitor,
              fair_id: fairId,
            },
            pageContext: 'exhibitor-details',
          })
        );
      })
    )
  );

  loadProductWatchlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.loadWatchlistEntity),
      filter((action) => action.watchlistItem.entity_type === 'PRODUCT'),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(DataAccessFairData.getProductById, { productId: action.watchlistItem.entity_id }))
          )
        )
      ),
      switchMap(([action, product]) =>
        (product
          ? of(product)
          : this.productsService.getProduct(action.watchlistItem.fair_id, action.watchlistItem.entity_id)
        ).pipe(
          switchMap((product) =>
            of(product).pipe(
              withLatestFrom(
                this.store.pipe(select(DataAccessFairData.getExhibitorById, { exhibitorId: product.exhibitor_id })),
                of(action.watchlistItem.fair_id)
              )
            )
          ),
          switchMap(([product, exhibitor, fairId]) =>
            (exhibitor ? of(exhibitor) : this.exhibitorService.getExhibitor(fairId, product.exhibitor_id)).pipe(
              map((exhibitor) =>
                WatchlistActions.loadWatchlistEntitySuccess({
                  ...action,
                  exhibitor: exhibitor,
                  product: product,
                })
              ),
              catchError(() => of(WatchlistActions.loadWatchlistEntityFailure(action)))
            )
          ),
          catchError(() => of(WatchlistActions.loadWatchlistEntityFailure(action)))
        )
      )
    )
  );

  generateWatchlistDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.generateWatchlistDocument),
      withLatestFrom(this.userSessionStatus),
      filter(([_, activeSession]) => activeSession),
      mergeMap(() =>
        this.watchlistService.generateWatchlistDocument().pipe(
          map((result) =>
            WatchlistActions.generateWatchlistDocumentSuccess({
              documentId: result.document_id,
            })
          ),
          catchError(() => of(WatchlistActions.generateWatchlistDocumentFailure()))
        )
      )
    )
  );

  constructor(
    private store: Store<DataAccessFairData.EventsState>,
    private readonly actions$: Actions,
    private watchlistService: WatchlistService,
    private eventsService: DataAccessEventsService,
    private exhibitorService: DataAccessExhibitorService,
    private notificationService: NotificationService,
    private postsService: DataAccessPostsService,
    private productsService: DataAccessProductsService,
    private fairsFacade: DataAccessFairsFacade,
    private exhibitorFacade: DataAccessExhibitorsFacade,
    private productFacade: DataAccessProductsFacade,
    private features: DataAccessTogglesFacade,
    private activatedRoute: ActivatedRoute,
    @Inject(USER_SESSION_STATUS) private userSessionStatus: BehaviorSubject<boolean>
  ) {}
}
